import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { clinicInfo } from '@/mocks/homeData';

export default function HeroSection() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start start', 'end start'],
  });

  const imageY = useTransform(scrollYProgress, [0, 1], ['0%', '25%']);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.4], [1, 0]);
  const contentY = useTransform(scrollYProgress, [0, 0.4], ['0%', '8%']);
  const overlayOpacity = useTransform(scrollYProgress, [0, 0.5], [1, 1.3]);

  return (
    <section ref={ref} className="relative h-[85vh] min-h-[560px] md:h-screen md:min-h-[700px] w-full overflow-hidden">
      {/* Background Image with Enhanced Parallax */}
      <motion.div
        style={{ y: imageY, scale: overlayOpacity }}
        className="absolute inset-0 z-0"
      >
        <img
          src="https://storage.readdy-site.link/project_files/ed85e06b-2b23-49e7-a477-ed0f88fc71a8/70af4940-2422-466d-953f-ea16a899c7a9_compressed_ChatGPT-Image-22-de-jul.-de-2026-.webp"
          alt="Femme Vie - Clínica de Saúde Feminina"
          className="w-full h-full object-cover object-center md:object-top"
        />
      </motion.div>

      {/* Dark Overlay */}
      <div className="absolute inset-0 z-[1] bg-gradient-to-b from-black/30 via-black/20 to-black/40" />

      {/* Floating decorative elements */}
      <motion.div
        animate={{
          y: [-6, 6, -6],
          rotate: [-1, 1, -1],
        }}
        transition={{
          repeat: Infinity,
          duration: 10,
          ease: 'easeInOut',
        }}
        className="absolute top-[15%] left-[8%] z-[2] w-40 h-40 rounded-full border border-white/5 hidden lg:block"
      />
      <motion.div
        animate={{
          y: [8, -8, 8],
          rotate: [2, -2, 2],
        }}
        transition={{
          repeat: Infinity,
          duration: 12,
          ease: 'easeInOut',
        }}
        className="absolute top-[25%] right-[10%] z-[2] w-28 h-28 rounded-full bg-white/3 hidden lg:block"
      />

      {/* Content */}
      <motion.div
        style={{ opacity: contentOpacity, y: contentY }}
        className="relative z-10 h-full flex flex-col items-center justify-center text-center px-6 md:px-10"
      >
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
          className="text-white/80 text-xs md:text-sm tracking-[0.2em] uppercase mb-4 font-label"
        >
          Ginecologia Avançada & Saúde Feminina
        </motion.p>

        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
          className="font-heading text-4xl md:text-6xl lg:text-7xl text-white leading-[1.08] max-w-[800px] text-balance"
        >
          Cuidar da mulher<br />
          <span className="italic font-light text-white/90">em todas as fases da vida.</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
          className="mt-6 text-white/65 text-sm md:text-base max-w-[480px] leading-relaxed font-light"
        >
          Uma clínica boutique pensada para acolher, cuidar e transformar a sua saúde com excelência, tecnologia e sensibilidade.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
          className="flex flex-col sm:flex-row items-center gap-4 mt-10"
        >
          <a
            href={`https://wa.me/${clinicInfo.whatsapp}`}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center gap-3 px-8 py-3.5 rounded-full bg-white text-foreground-950 text-sm tracking-[0.06em] uppercase whitespace-nowrap hover:bg-white/90 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
          >
            <span>Agendar Consulta</span>
            <i className="ri-arrow-right-line transition-all duration-300 group-hover:translate-x-1"></i>
          </a>
          <a
            href="#especialidades"
            className="flex items-center gap-3 px-8 py-3.5 rounded-full border border-white/30 text-white text-sm tracking-[0.06em] uppercase whitespace-nowrap hover:bg-white/10 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
          >
            <span>Conhecer Especialidades</span>
          </a>
        </motion.div>
      </motion.div>

      {/* Scroll Indicator — refined */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.3, duration: 0.8 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-3"
      >
        <span className="text-white/45 text-[10px] tracking-[0.2em] uppercase font-label">Scroll</span>
        <motion.div
          animate={{ y: [0, 10, 0], opacity: [0.4, 1, 0.4] }}
          transition={{ repeat: Infinity, duration: 2.2, ease: 'easeInOut' }}
          className="flex flex-col items-center gap-1.5"
        >
          <span className="block w-[1px] h-5 bg-gradient-to-b from-white/0 to-white/50" />
          <span className="block w-[1px] h-4 bg-gradient-to-b from-white/50 to-white/20" />
        </motion.div>
      </motion.div>
    </section>
  );
}